import React, { Component } from "react";
import i18n from "i18next";
import { isSlide } from "../../../../common/utils";
import EditorIndexTitle from "../editor_index_title/EditorIndexTitle";
import PropTypes from "prop-types";
import CarouselList from "../carousel_list/CarouselList";
import "./_containedViewsList.scss";

const getParentNavItemIndex = (cvId, containedViews, boxes, navItems, navItemsIds) => {
  const cv = containedViews[cvId];
  if (!cv || !cv.parent) return "";

  // Get first parent box ID from the parent map
  const parentBoxIds = Object.values(cv.parent);
  if (parentBoxIds.length === 0) return "";

  const firstParentBoxId = parentBoxIds[0];
  const parentBox = boxes[firstParentBoxId];
  if (!parentBox) return "";

  // Get the navItem that this box belongs to
  const parentNavItemId = parentBox.parent;
  const parentNavItem = navItems[parentNavItemId];
  if (!parentNavItem) return "";

  // Calculate the index number of the parent navItem
  const indexParts = [];

  // Build path to parent navItem
  let currentId = parentNavItemId;
  const pathIds = [];
  while (currentId && currentId !== 0) {
    pathIds.unshift(currentId);
    const current = navItems[currentId];
    if (current && current.parent !== undefined) {
      currentId = current.parent;
    } else {
      break;
    }
  }

  // Calculate hierarchical index
  for (let i = 0; i < pathIds.length; i++) {
    const id = pathIds[i];
    const item = navItems[id];
    if (item && item.parent !== undefined) {
      const parent = navItems[item.parent];
      if (parent && parent.children) {
        const index = parent.children.indexOf(id) + 1;
        indexParts.push(index);
      }
    }
  }

  return indexParts.join(".");
};

export default class ContainedViewsList extends Component {
  compareHierarchicalIndex(a, b) {
    if (!a && !b) return 0;
    if (!a) return 1;
    if (!b) return -1;

    const aParts = a.split(".").map(Number);
    const bParts = b.split(".").map(Number);

    const maxLength = Math.max(aParts.length, bParts.length);

    for (let i = 0; i < maxLength; i++) {
      const aVal = aParts[i] || 0;
      const bVal = bParts[i] || 0;

      if (aVal < bVal) return -1;
      if (aVal > bVal) return 1;
    }
    return 0;
  }

  render() {
    let containedViewsIncluded = Object.keys(this.props.containedViews).length > 0;

    const cvGroups = {};
    const cvIndexMap = {};
    const cvParentIndexMap = {};

    Object.keys(this.props.containedViews).forEach((id) => {
      const parentIndex = getParentNavItemIndex(
        id,
        this.props.containedViews,
        this.props.boxes,
        this.props.navItems,
        this.props.navItemsIds
      );

      cvParentIndexMap[id] = parentIndex || "";
      const groupKey = parentIndex || "no-parent";

      if (!cvGroups[groupKey]) {
        cvGroups[groupKey] = [];
      }
      cvGroups[groupKey].push(id);
    });

    Object.keys(cvGroups).forEach((groupKey) => {
      cvGroups[groupKey].forEach((id, index) => {
        const parentIndex = getParentNavItemIndex(
          id,
          this.props.containedViews,
          this.props.boxes,
          this.props.navItems,
          this.props.navItemsIds
        );

        const cvIndex = parentIndex ? `${parentIndex}/${index + 1}` : `${index + 1}`;
        cvIndexMap[id] = cvIndex;
      });
    });

    const sortedCvIds = Object.keys(this.props.containedViews).sort((a, b) => {
      const aParent = cvParentIndexMap[a] || "";
      const bParent = cvParentIndexMap[b] || "";

      const parentCompare = this.compareHierarchicalIndex(aParent, bParent);
      if (parentCompare !== 0) return parentCompare;

      const aGroup = aParent || "no-parent";
      const bGroup = bParent || "no-parent";
      if (aGroup === bGroup) {
        const aIndex = cvGroups[aGroup].indexOf(a);
        const bIndex = cvGroups[bGroup].indexOf(b);
        return aIndex - bIndex;
      }

      return 0;
    });

    return (
      <div
        className="containedViewsList"
        style={{
          maxHeight: this.props.contentHeight || "0px",
          height: this.props.showContainedViews ? this.props.contentHeight : "0px",
          padding: this.props.showContainedViews ? "0 16px 16px 16px" : "0",
        }}
      >
        <div className={`empty-info ${containedViewsIncluded ? "hidden" : "visible"}`}>
          {i18n.t("empty.cv_empty")}
        </div>

        {sortedCvIds.map((id) => {
          if (!this.props.viewToolbars[id]) return null;

          let classIndexSelected = id === this.props.indexSelected ? " classIndexSelected " : " ";
          let containedViewSelected = id === this.props.containedViewSelected ? " selected " : " ";
          const isSelected = id === this.props.indexSelected;

          const cvIndex = cvIndexMap[id] || "1";

          return (
            <div className={"carousselContainer " + containedViewSelected} key={id}>
              <div
                key={id}
                className={"file navItemBlock " + classIndexSelected}
                onMouseDown={(e) => {
                  this.props.onIndexSelected(id);
                }}
                onDoubleClick={(e) => {
                  this.props.onIndexSelected(id);
                  e.stopPropagation();
                }}
              >
                <div className={`item-number ${isSelected ? "selected" : ""}`}>{cvIndex}</div>
                <div className="item-divider" />
                <EditorIndexTitle
                  id={id}
                  title={this.props.viewToolbars[id].viewName}
                  index=""
                  hidden={false}
                  selected={this.props.containedViewSelected}
                  onNameChanged={this.props.onContainedViewNameChanged}
                />
                <i className="material-icons fileIcon">
                  {isSlide(this.props.containedViews[id].type) ? "slideshow" : "insert_drive_file"}
                </i>
              </div>
            </div>
          );
        })}
      </div>
    );
  }
}

ContainedViewsList.propTypes = {
  /**
   *  Object containing all contained views (identified by its ID)
   */
  containedViews: PropTypes.object.isRequired,
  /**
   * Selected contained view
   */
  containedViewSelected: PropTypes.any,
  /**
   * View/Contained view selected at the index
   */
  indexSelected: PropTypes.any,
  /**
   * Callback for selecting contained view
   */
  onContainedViewNameChanged: PropTypes.func.isRequired,
  /**
   * Callback for renaming contained view
   */
  onContainedViewSelected: PropTypes.func.isRequired,
  /**
   * Callback for renaming view
   */
  onIndexSelected: PropTypes.func.isRequired,
  /**
   * Object containing all the pages' toolbars
   */
  viewToolbars: PropTypes.object.isRequired,
  /**
   * Indicates if contained views should be displayed
   */
  showContainedViews: PropTypes.bool.isRequired,
  /**
   * Indicates if sortable items should be displayed
   */
  showSortableItems: PropTypes.bool.isRequired,
  /**
   * Object containing all boxes
   */
  boxes: PropTypes.object.isRequired,
  /**
   * Dictionary containing all created views, each one with its *id* as the key
   */
  navItems: PropTypes.object.isRequired,
  /**
   * Array of all navItem IDs in order
   */
  navItemsIds: PropTypes.array.isRequired,
  /**
   * Calculated content height for the section
   */
  contentHeight: PropTypes.string,
};
